import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CITIES, DISCIPLINES, STANCES, LEVELS, GOVERNING_BODIES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Crosshair, Shield } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1716307043003-dbe6a5cc496e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const VoltButton = ({ children, ...props }) => (
  <button
    {...props}
    className="w-full bg-volt text-black font-heading font-bold uppercase tracking-widest text-lg py-3 rounded-sm transition-colors duration-200 hover:bg-volt-hover disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-volt focus:ring-offset-2 focus:ring-offset-background"
  >
    {children}
  </button>
);

const field =
  "bg-[#0d0d0d] border-[#27272a] text-white h-11 focus:ring-2 focus:ring-volt focus:border-volt";

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className={field}>
          <SelectValue placeholder={`Select ${label}`} />
        </SelectTrigger>
        <SelectContent className="bg-[#161616] border-[#27272a] text-white">
          {options.map((o) => (
            <SelectItem key={o} value={o} className="focus:bg-volt focus:text-black">
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function AuthPage() {
  const { login, register, registerTrainer } = useAuth();
  const [mode, setMode] = useState("login");
  const [accountType, setAccountType] = useState("fighter");
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [form, setForm] = useState({
    fighter_name: "", gym_name: "", city: "", discipline: "",
    stance: "", weight: "", weight_unit: "kg", height: "",
    experience_level: "",
  });
  const [trainerForm, setTrainerForm] = useState({
    name: "", full_legal_name: "", gym_name: "", phone: "", city: "",
    discipline: "", governing_body: "", coach_license: "", bio: "",
  });
  const [tAgree, setTAgree] = useState({ tos: false, privacy: false, conduct: false, credentials: false });
  const [waiver, setWaiver] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setT = (k) => (v) => setTrainerForm((f) => ({ ...f, [k]: v }));

  const handleRegisterTrainer = async (e) => {
    e.preventDefault();
    for (const [k, label] of [["name", "trainer name"], ["full_legal_name", "full legal name"], ["gym_name", "gym"], ["phone", "phone"], ["city", "city"], ["discipline", "discipline"], ["governing_body", "governing body"]]) {
      if (!trainerForm[k]) { toast.error(`Please provide your ${label}.`); return; }
    }
    if (!(tAgree.tos && tAgree.privacy && tAgree.conduct && tAgree.credentials)) {
      toast.error("You must accept all agreements to register as a trainer.");
      return;
    }
    setLoading(true);
    const res = await registerTrainer({
      email, password, ...trainerForm,
      agree_tos: tAgree.tos, agree_privacy: tAgree.privacy,
      agree_conduct: tAgree.conduct, attest_credentials: tAgree.credentials,
    });
    setLoading(false);
    if (res.ok) toast.success("Trainer profile live. Opening your dashboard.");
    else toast.error(res.error);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { toast.error("Enter your email."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success(data.message || "Check your inbox for a reset link.");
      setMode("login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) toast.success("Welcome back to the mat.");
    else toast.error(res.error);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!waiver) {
      toast.error("You must accept the liability waiver to sign up.");
      return;
    }
    const required = ["fighter_name", "gym_name", "city", "discipline", "stance", "weight", "height", "experience_level"];
    for (const r of required) {
      if (!form[r]) { toast.error("Complete all fighter details."); return; }
    }
    setLoading(true);
    const res = await register({
      email, password,
      ...form,
      weight: parseFloat(form.weight),
      waiver_accepted: true,
    });
    setLoading(false);
    if (res.ok) toast.success("Profile live. Entering the radar.");
    else toast.error(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 relative z-10">
      {/* Left / hero */}
      <div className="relative hidden lg:block">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 tech-grid opacity-40" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2">
            <Crosshair className="text-volt" size={28} strokeWidth={2.5} />
            <span className="font-heading font-black text-2xl uppercase tracking-tight">Spar<span className="text-volt">Base</span></span>
          </Link>
          <div>
            <h1 className="font-heading font-black uppercase text-6xl leading-[0.9] tracking-tighter">
              Find your<br />next round.
            </h1>
            <p className="mt-4 text-zinc-300 max-w-md">
              The UK's peer-to-peer sparring directory. Match by gym, weight, discipline
              and stance — then request rounds. Built for fighters, by fighters.
            </p>
          </div>
          <p className="text-xs text-zinc-500 uppercase tracking-widest">Combat sports · United Kingdom</p>
        </div>
      </div>

      {/* Right / form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background overflow-y-auto">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <Crosshair className="text-volt" size={26} strokeWidth={2.5} />
            <span className="font-heading font-black text-2xl uppercase">Spar<span className="text-volt">Base</span></span>
          </Link>

          <div className="flex gap-1 mb-8 border border-[#27272a] rounded-sm p-1 bg-[#161616]">
            {["login", "signup"].map((m) => (
              <button
                key={m}
                data-testid={`tab-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 py-2.5 font-heading uppercase tracking-wider text-sm rounded-sm transition-colors duration-200 ${
                  mode === m ? "bg-volt text-black font-bold" : "text-zinc-400 hover:text-white"
                }`}
              >
                {m === "login" ? "Log In" : "Sign Up"}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {mode === "login" ? (
              <motion.form
                key="login"
                initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }}
                transition={{ duration: 0.2 }}
                onSubmit={handleLogin} className="space-y-4"
              >
                <h2 className="font-heading text-3xl uppercase tracking-tight">Enter the gym</h2>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">Email</Label>
                  <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} placeholder="you@gym.uk" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">Password</Label>
                  <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={field} placeholder="••••••••" />
                </div>
                <VoltButton data-testid="login-submit" type="submit" disabled={loading}>
                  {loading ? "Checking…" : "Log In"}
                </VoltButton>
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => setMode("forgot")}
                  className="w-full text-center text-xs text-zinc-500 hover:text-volt transition-colors duration-200 uppercase tracking-wider"
                >
                  Forgot password?
                </button>
              </motion.form>
            ) : mode === "forgot" ? (
              <motion.form
                key="forgot"
                initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }}
                transition={{ duration: 0.2 }}
                onSubmit={handleForgot} className="space-y-4"
              >
                <h2 className="font-heading text-3xl uppercase tracking-tight">Reset password</h2>
                <p className="text-sm text-zinc-400">
                  Enter your email and we'll send you a link to set a new password.
                </p>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">Email</Label>
                  <Input data-testid="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className={field} placeholder="you@gym.uk" />
                </div>
                <VoltButton data-testid="forgot-submit" type="submit" disabled={loading}>
                  {loading ? "Sending…" : "Send Reset Link"}
                </VoltButton>
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className="w-full text-center text-xs text-zinc-500 hover:text-volt transition-colors duration-200 uppercase tracking-wider"
                >
                  ← Back to log in
                </button>
              </motion.form>
            ) : (
              <motion.form
                key="signup"
                initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
                onSubmit={accountType === "trainer" ? handleRegisterTrainer : handleRegister} className="space-y-4"
              >
                <h2 className="font-heading text-3xl uppercase tracking-tight">Build your profile</h2>

                <div className="flex gap-1 border border-[#27272a] rounded-sm p-1 bg-[#0d0d0d]">
                  {[["fighter", "Fighter"], ["trainer", "Gym Trainer"]].map(([v, l]) => (
                    <button
                      key={v}
                      type="button"
                      data-testid={`account-type-${v}`}
                      onClick={() => setAccountType(v)}
                      className={`flex-1 py-2 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors duration-200 ${
                        accountType === v ? "bg-volt text-black font-bold" : "text-zinc-400 hover:text-white"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">Email</Label>
                    <Input data-testid="signup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} placeholder="you@gym.uk" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">Password</Label>
                    <Input data-testid="signup-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={field} placeholder="min 6 chars" />
                  </div>
                </div>
                {accountType === "fighter" && (
                <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">Fighter Name</Label>
                    <Input data-testid="signup-name" value={form.fighter_name} onChange={(e) => set("fighter_name")(e.target.value)} className={field} placeholder="Nickname" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">Gym</Label>
                    <Input data-testid="signup-gym" value={form.gym_name} onChange={(e) => set("gym_name")(e.target.value)} className={field} placeholder="Your gym" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <SelectField label="City / Region" value={form.city} onChange={set("city")} options={CITIES} testid="signup-city" />
                  <SelectField label="Discipline" value={form.discipline} onChange={set("discipline")} options={DISCIPLINES} testid="signup-discipline" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <SelectField label="Stance" value={form.stance} onChange={set("stance")} options={STANCES} testid="signup-stance" />
                  <SelectField label="Experience" value={form.experience_level} onChange={set("experience_level")} options={LEVELS} testid="signup-level" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5 col-span-1">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">Weight</Label>
                    <Input data-testid="signup-weight" type="number" value={form.weight} onChange={(e) => set("weight")(e.target.value)} className={field} placeholder="72" />
                  </div>
                  <div className="space-y-1.5 col-span-1">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">Unit</Label>
                    <Select value={form.weight_unit} onValueChange={set("weight_unit")}>
                      <SelectTrigger data-testid="signup-unit" className={field}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#161616] border-[#27272a] text-white">
                        <SelectItem value="kg" className="focus:bg-volt focus:text-black">kg</SelectItem>
                        <SelectItem value="lbs" className="focus:bg-volt focus:text-black">lbs</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5 col-span-1">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">Height</Label>
                    <Input data-testid="signup-height" value={form.height} onChange={(e) => set("height")(e.target.value)} className={field} placeholder={`5'10"`} />
                  </div>
                </div>
                </>
                )}

                {accountType === "trainer" && (
                <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">Trainer Name</Label>
                    <Input data-testid="trainer-name" value={trainerForm.name} onChange={(e) => setT("name")(e.target.value)} className={field} placeholder="Coach name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">Gym</Label>
                    <Input data-testid="trainer-gym" value={trainerForm.gym_name} onChange={(e) => setT("gym_name")(e.target.value)} className={field} placeholder="Your gym" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <SelectField label="City / Region" value={trainerForm.city} onChange={setT("city")} options={CITIES} testid="trainer-city" />
                  <SelectField label="Primary Discipline" value={trainerForm.discipline} onChange={setT("discipline")} options={DISCIPLINES} testid="trainer-discipline" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">Full Legal Name</Label>
                    <Input data-testid="trainer-legal-name" value={trainerForm.full_legal_name} onChange={(e) => setT("full_legal_name")(e.target.value)} className={field} placeholder="As on ID" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">UK Phone</Label>
                    <Input data-testid="trainer-phone" value={trainerForm.phone} onChange={(e) => setT("phone")(e.target.value)} className={field} placeholder="07… or +44…" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <SelectField label="Governing Body" value={trainerForm.governing_body} onChange={setT("governing_body")} options={GOVERNING_BODIES} testid="trainer-governing-body" />
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">Coach Licence # (optional)</Label>
                    <Input data-testid="trainer-license" value={trainerForm.coach_license} onChange={(e) => setT("coach_license")(e.target.value)} className={field} placeholder="If you have one" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">Bio (optional)</Label>
                  <Input data-testid="trainer-bio" value={trainerForm.bio} onChange={(e) => setT("bio")(e.target.value)} className={field} placeholder="Coaching background, achievements…" />
                </div>
                </>
                )}

                {accountType === "fighter" && (
                <label
                  data-testid="waiver-checkbox-wrap"
                  className={`flex gap-3 items-start p-4 rounded-sm border cursor-pointer transition-colors duration-200 ${
                    waiver ? "border-volt bg-volt/5" : "border-[#27272a] bg-[#161616]"
                  }`}
                >
                  <Checkbox data-testid="waiver-checkbox" checked={waiver} onCheckedChange={setWaiver} className="mt-0.5 border-zinc-500 data-[state=checked]:bg-volt data-[state=checked]:border-volt data-[state=checked]:text-black" />
                  <span className="text-xs text-zinc-300 leading-relaxed flex items-start gap-1">
                    <Shield size={14} className="text-volt shrink-0 mt-0.5" />
                    I agree that SparBase is a digital networking directory only. SparBase takes zero
                    liability for injuries or disputes. All users spar entirely at their own risk.
                  </span>
                </label>
                )}

                {accountType === "trainer" && (
                <div className="space-y-2">
                  {[
                    ["tos", <>I agree to the <a href="/terms" target="_blank" rel="noreferrer" className="text-volt underline">Terms of Service &amp; Disclaimer</a> — SparBase is a coordination tool and is not liable for injuries, matching outcomes or gym disputes.</>, "trainer-agree-tos"],
                    ["privacy", <>I agree to the <a href="/privacy" target="_blank" rel="noreferrer" className="text-volt underline">Privacy Policy</a> and how my data and my fighters' data is handled under UK GDPR.</>, "trainer-agree-privacy"],
                    ["conduct", <>I confirm I am an authorised coach/gym representative responsible for the safety and supervision of the fighters in my stable.</>, "trainer-agree-conduct"],
                    ["credentials", <>I confirm I hold valid coaching credentials and insurance to oversee sparring, and the details I've provided are accurate.</>, "trainer-agree-credentials"],
                  ].map(([key, text, tid]) => (
                    <label
                      key={key}
                      className={`flex gap-3 items-start p-3 rounded-sm border cursor-pointer transition-colors duration-200 ${
                        tAgree[key] ? "border-volt bg-volt/5" : "border-[#27272a] bg-[#161616]"
                      }`}
                    >
                      <Checkbox data-testid={tid} checked={tAgree[key]} onCheckedChange={(v) => setTAgree((a) => ({ ...a, [key]: !!v }))} className="mt-0.5 border-zinc-500 data-[state=checked]:bg-volt data-[state=checked]:border-volt data-[state=checked]:text-black" />
                      <span className="text-xs text-zinc-300 leading-relaxed">{text}</span>
                    </label>
                  ))}
                </div>
                )}

                <VoltButton data-testid="signup-submit" type="submit" disabled={loading}>
                  {loading ? "Creating…" : accountType === "trainer" ? "Create Trainer Profile" : "Create Fighter Profile"}
                </VoltButton>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
